import { useEffect, useRef, useState } from 'react';
import FundOpportunityChatResponse, {
  FundOpportunityChatResponseAddress,
} from 'FundOpportunityChatResponse';
import { FundOpportunityLabels } from '../Util/Labels';
import {
  formatPopulatedFieldValue,
  preferredFieldOrder,
} from './populatedFundOpportunityFieldFormatters';

export function usePopulatedFundOpportunityFieldAnimation(
  data?: FundOpportunityChatResponse['currentData'],
) {
  const previousValuesRef = useRef<Record<string, string>>({});
  const activeTimeoutsRef = useRef<Record<string, NodeJS.Timeout[]>>({});
  const [displayedFieldLines, setDisplayedFieldLines] = useState<Record<string, string>>({});
  const [animatedKeys, setAnimatedKeys] = useState<Set<string>>(new Set());
  const [renderedKeys, setRenderedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!data) return;

    const animationsToRun: Array<{
      key: string;
      label: string;
      newValue: string;
      isNew: boolean;
    }> = [];

    const validFields = Object.entries(data).filter(([key, rawField]) => {
      if (key === 'tempFundOpportunityId') return false;

      const val = 'value' in (rawField as any) ? (rawField as any).value : rawField;
      return (
        val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0)
      );
    });

    const addressesField = validFields.find(([key]) => key === 'addresses');
    const currentAddresses = addressesField
      ? 'value' in (addressesField[1] as any)
        ? (addressesField[1] as any).value
        : addressesField[1]
      : [];
    const currentAddressJson = JSON.stringify(currentAddresses);
    const prevAddressJson = JSON.stringify(previousValuesRef.current.__addressRaw || []);
    const addressesChanged = currentAddressJson !== prevAddressJson;

    if (addressesChanged) {
      previousValuesRef.current.__addressRaw = currentAddresses;

      Object.keys(activeTimeoutsRef.current).forEach((key) => {
        if (key.startsWith('address-')) {
          activeTimeoutsRef.current[key]?.forEach(clearTimeout);
          delete activeTimeoutsRef.current[key];
        }
      });

      Object.keys(previousValuesRef.current).forEach((key) => {
        if (key.startsWith('address-')) {
          delete previousValuesRef.current[key];
        }
      });

      setDisplayedFieldLines((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (key.startsWith('address-')) {
            delete updated[key];
          }
        });
        return updated;
      });

      setAnimatedKeys((prev) => {
        const next = new Set(prev);
        Array.from(next).forEach((key) => {
          if (key.startsWith('address-')) {
            next.delete(key);
          }
        });
        return next;
      });
    }

    for (const [key, rawField] of validFields) {
      const val = 'value' in (rawField as any) ? (rawField as any).value : rawField;
      const label = FundOpportunityLabels[key] || key;

      if (key === 'addresses' && Array.isArray(val)) {
        val.forEach((address: FundOpportunityChatResponseAddress, index: number) => {
          const addressKey = `address-${index}-${address.placeId}`;
          const addressValue = address.fullAddress;
          const isNew = addressesChanged || !(addressKey in previousValuesRef.current);

          animationsToRun.push({
            key: addressKey,
            label: `Address ${index + 1}`,
            newValue: addressValue,
            isNew,
          });

          previousValuesRef.current[addressKey] = addressValue;
        });
      } else {
        const formatted = formatPopulatedFieldValue(rawField, key);
        const prev = previousValuesRef.current[key];

        if (prev === undefined) {
          animationsToRun.push({
            key,
            label,
            newValue: formatted,
            isNew: true,
          });
        } else if (prev !== formatted) {
          animationsToRun.push({
            key,
            label,
            newValue: formatted,
            isNew: false,
          });
        }

        previousValuesRef.current[key] = formatted;
      }
    }

    const shouldUsePreferredOrder = validFields.length > 1;

    animationsToRun.forEach(({ key, newValue }) => {
      setAnimatedKeys((prev) => new Set(prev).add(key));

      setDisplayedFieldLines((prev) => ({
        ...prev,
        [key]: '',
      }));

      const chars = newValue.split('');

      if (!activeTimeoutsRef.current[key]) {
        activeTimeoutsRef.current[key] = [];
      }

      chars.forEach((char, idx) => {
        const timeoutId = setTimeout(() => {
          setDisplayedFieldLines((prev) => ({
            ...prev,
            [key]: (prev[key] || '') + char,
          }));

          if (idx === chars.length - 1) {
            setAnimatedKeys((prev) => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            });
            delete activeTimeoutsRef.current[key];
          }
        }, 50 * idx);

        activeTimeoutsRef.current[key].push(timeoutId);
      });
    });

    if (shouldUsePreferredOrder) {
      const orderedKeys = preferredFieldOrder.filter((key) =>
        validFields.some(([fieldKey]) => fieldKey === key),
      );
      setRenderedKeys(orderedKeys);
    } else {
      animationsToRun.forEach(({ key }) => {
        const baseKey = key.startsWith('address-') ? 'addresses' : key;
        setRenderedKeys((prev) => {
          if (!prev.includes(baseKey)) {
            return [...prev, baseKey];
          }
          return prev;
        });
      });
    }
  }, [data]);

  useEffect(() => {
    return () => {
      Object.values(activeTimeoutsRef.current).forEach((timeouts) => {
        timeouts.forEach(clearTimeout);
      });
    };
  }, []);

  return { displayedFieldLines, animatedKeys, renderedKeys };
}
